import { useEffect, useState } from 'react';

/**
 * Matches a media query, and keeps matching it as the window changes.
 *
 * Deliberately a JS query rather than rendering both trees and hiding one with `sm:hidden`: where
 * this is used the two layouts are different DOM, and paying to build both on every render — then
 * asking antd to lay out a table nobody will see — is worse than one listener.
 *
 * In its own module rather than beside its only caller because a hook exported from a file that
 * also exports components breaks React Fast Refresh, which the lint rule catches.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);

    // Re-read on mount as well as on change: the query string can differ from the one the initial
    // state was computed with, and a resize between render and effect would otherwise be missed.
    onChange();
    list.addEventListener('change', onChange);

    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
